import { FC, useCallback, useDeferredValue, useEffect, useRef, useState } from 'react';

import cat1Image from 'assets/cat1.png';

import {
    ActionBar,
    BottomSheet,
    Button,
    ListControls,
    Modal,
    NavigationBar,
    Placeholder,
    Text,
    TreeFilter,
    TreeSelector,
    TreeSelectorProps,
} from '@hh.ru/magritte-ui';
import { ChevronLeftOutlinedSize24, CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { useCompositeSelectionContext } from 'components/common/CompositeSelection/contexts';
import { filterByExcludedValue } from 'components/common/CompositeSelection/utils';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

interface TreeSelectorModalProps {
    maxSelected?: number;
    leavesOnly?: boolean;
    collapseToParentId?: boolean;
    singleChoice?: boolean;
    suggestedNotFound?: string[];
    constantlySuggested?: string[];
    texts?: {
        title?: string;
        titleDescription?: string;
        submit?: string;
        cancel?: string;
        searchPlaceholder?: string;
        notFound?: string;
        selectAll?: string;
    };
    treeFilter?: TreeFilter;
    renderContentBefore?: (isSearch: boolean) => React.ReactNode;
    onChangeFilterQuery?: (ids: string[], query: string) => void;
    disabled?: string[];
    rootElementControl?: (state: boolean) => void;
}

const TrlKeys = {
    title: 'compositeSelection.treeSelector.title',
    submit: 'compositeSelection.treeSelector.submit',
    cancel: 'compositeSelection.treeSelector.cancel',
    searchPlaceholder: 'compositeSelection.treeSelector.searchPlaceholder',
    notFound: 'compositeSelection.treeSelector.notFound',
    selectAll: 'compositeSelection.treeSelector.selectAll',
};

const CompositeSelectionTreeSelectorModal: FC<TreeSelectorModalProps> = (props) => {
    const { collection, value, excludedValue, isTreeSelectorModalVisible, setTreeSelectorModalVisibility, onChange } =
        useCompositeSelectionContext();

    const {
        maxSelected,
        leavesOnly,
        singleChoice,
        collapseToParentId,
        suggestedNotFound,
        constantlySuggested,
        treeFilter,
        renderContentBefore,
        onChangeFilterQuery,
        disabled,
        rootElementControl,
    } = props;

    const deferredVisible = useDeferredValue(isTreeSelectorModalVisible);

    const trls = useTrls();

    const texts = {
        title: props?.texts?.title || trls(TrlKeys.title),
        titleDescription: props?.texts?.titleDescription,
        submit: props?.texts?.submit || trls(TrlKeys.submit),
        cancel: props?.texts?.cancel || trls(TrlKeys.cancel),
        searchPlaceholder: props?.texts?.searchPlaceholder || trls(TrlKeys.searchPlaceholder),
        notFound: props?.texts?.notFound || trls(TrlKeys.notFound),
        selectAll: props?.texts?.selectAll || trls(TrlKeys.selectAll),
    };

    const [selected, setSelected] = useState<string[]>(value);
    const controlsRef = useRef<ListControls>(null);
    const [mobileTitle, setMobileTitle] = useState<string | undefined>(texts.title);

    useEffect(() => {
        if (excludedValue && isTreeSelectorModalVisible) {
            const filteredValue = filterByExcludedValue(collection, value, excludedValue);
            setSelected(filteredValue);
        }
    }, [collection, value, excludedValue, setSelected, isTreeSelectorModalVisible]);

    const handleChange: TreeSelectorProps<never>['onChange'] = useCallback(
        (allSelected, id, isSelected) => {
            const isRootElement = collection.getParentId(id) === undefined;

            if (isRootElement) {
                // в данном случае колбек необходим для исключительно для реализации
                // логики выбора корневого региона без добавления в каталог нового элемента, как для других регионов
                rootElementControl?.(isSelected);
            }

            setSelected([...new Set(allSelected)]);
        },
        [collection, rootElementControl]
    );

    const handleCloseModal = useCallback(() => {
        setTreeSelectorModalVisibility(false);
        setSelected(value);
        setMobileTitle(undefined);
    }, [setTreeSelectorModalVisibility, value]);

    const handleSaveAndCloseModal = useCallback(() => {
        onChange(() => ({
            value: selected,
            excludedValue,
        }));

        handleCloseModal();
    }, [selected, excludedValue, handleCloseModal, onChange]);

    const handleMobileNavigationChange: NonNullable<TreeSelectorProps<never>['onMobileNavigationChange']> = useCallback(
        (id) => {
            const text = id ? collection.getModel(id)?.text : undefined;
            setMobileTitle(text);
        },
        [collection]
    );

    if (!isTreeSelectorModalVisible) {
        return null;
    }

    const contentNotFound = (
        <div>
            <Placeholder
                title=""
                paddingTop={56}
                titleProps={{
                    size: 'medium',
                    Element: 'h2',
                    alignment: 'center',
                    description: texts.notFound,
                }}
            >
                <Placeholder.Image title="" src={cat1Image} width={198} height={146} />
            </Placeholder>
        </div>
    );

    const footer = (
        <ActionBar
            primaryActions={
                <>
                    <Button
                        data-qa="composite-selection-tree-selector-cancel"
                        mode="secondary"
                        style="accent"
                        onClick={handleCloseModal}
                    >
                        {texts.cancel}
                    </Button>
                    <Button
                        data-qa="composite-selection-tree-selector-submit"
                        mode="primary"
                        style="accent"
                        onClick={handleSaveAndCloseModal}
                    >
                        {texts.submit}
                    </Button>
                </>
            }
        />
    );

    return (
        <TreeSelector
            collection={collection}
            value={selected}
            ref={controlsRef}
            maxSelected={maxSelected}
            leavesOnly={leavesOnly}
            collapseToParentId={collapseToParentId}
            singleChoice={singleChoice}
            suggestedNotFound={suggestedNotFound}
            constantlySuggested={constantlySuggested}
            contentNotFound={contentNotFound}
            renderContentBefore={renderContentBefore}
            treeFilter={treeFilter}
            getSelectAllParentTrl={() => texts.selectAll}
            onChangeFilterQuery={onChangeFilterQuery}
            onMobileNavigationChange={handleMobileNavigationChange}
            onChange={handleChange}
            disabled={disabled}
        >
            {({ renderTreeSelector, renderInput }) => {
                return (
                    <>
                        <Modal
                            visible={deferredVisible}
                            size="large"
                            header={
                                <MagritteModalHeader
                                    title={texts.title}
                                    subtitle={
                                        texts.titleDescription ? (
                                            <Text style="secondary" typography="label-2-regular">
                                                {texts.titleDescription}
                                            </Text>
                                        ) : undefined
                                    }
                                    actions={
                                        <CrossOutlinedSize24 initialColor="secondary" onClick={handleCloseModal} />
                                    }
                                    options={renderInput({ placeholder: texts.searchPlaceholder })}
                                />
                            }
                            onClose={handleCloseModal}
                            footer={footer}
                        >
                            {renderTreeSelector()}
                        </Modal>
                        <BottomSheet
                            visible={deferredVisible}
                            height="full-screen"
                            interceptClickHandlers={false}
                            header={
                                <NavigationBar
                                    title={mobileTitle || texts.title}
                                    right={<CrossOutlinedSize24 onClick={handleCloseModal} />}
                                    left={
                                        mobileTitle ? (
                                            <ChevronLeftOutlinedSize24 onClick={() => controlsRef.current?.back()} />
                                        ) : undefined
                                    }
                                    options={renderInput({ placeholder: texts.searchPlaceholder })}
                                />
                            }
                            footer={footer}
                            onClose={handleCloseModal}
                        >
                            {renderTreeSelector()}
                        </BottomSheet>
                    </>
                );
            }}
        </TreeSelector>
    );
};

export { CompositeSelectionTreeSelectorModal };
